// screens/AdminExpensesScreen.js
// ============================================================================
// Operating Expense Ledger (admin-only)
//
// CRUD interface for vendor invoices (GCP, Mux, Anthropic, OpenAI, Gemini,
// AWS, TwelveLabs, SendGrid, Persona, etc.). Mirrors AdminReconciliationScreen
// patterns: local error state (no ErrorContext), inline StyleSheet,
// onAuthStateChanged guard, Pressable-based two-tap confirms.
//
// Stripe processor fees are intentionally NOT listed here — they're computed
// live from deposits/{*}.stripeFeeCents and surfaced on FinancialsScreen's
// Operating Expenses section as a virtual line item. Attempting to create an
// expense with vendor='stripe_fees' is rejected by the backend.
// ============================================================================

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Pressable,
  ActivityIndicator, ScrollView, Modal, TextInput, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { httpsCallable, getFunctions } from 'firebase/functions';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

import {
  EXPENSE_VENDORS,
  EXPENSE_CATEGORIES,
  PAID_FROM_ACCOUNTS,
  VENDOR_OPTIONS,
  CATEGORY_OPTIONS,
  PAID_FROM_OPTIONS,
} from '../constants/expenseCategories';

const COLORS = {
  bg: '#000000',
  card: '#111111',
  cardAlt: '#0a0a0a',
  border: '#1f1f1f',
  borderLight: '#2a2a2a',
  primary: '#FCD000',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  text: '#ffffff',
  textMuted: '#888888',
  textDim: '#555555',
};

// ============================================================================
// FORMATTING HELPERS
// ============================================================================

const fmtCents = (cents) => {
  const n = Number(cents) || 0;
  const sign = n < 0 ? '-' : '';
  return `${sign}$${(Math.abs(n) / 100).toFixed(2)}`;
};

const fmtDate = (ms) => {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
};

const fmtMonth = (ms) => {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short', year: 'numeric', timeZone: 'UTC',
  });
};

// Convert a YYYY-MM-DD string (typed by admin) to milliseconds-since-epoch
// interpreted as UTC midnight. Returns null for empty/invalid input.
function dateStringToMs(str) {
  if (!str || typeof str !== 'string') return null;
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  if (!Number.isFinite(ms)) return null;
  return ms;
}

function msToDateString(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const da = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

function dollarsToCents(str) {
  if (str === '' || str === null || str === undefined) return null;
  const n = Number(str);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

// ============================================================================
// MAIN SCREEN
// ============================================================================

export default function AdminExpensesScreen({ navigation }) {
  const auth = getAuth();
  const functions = getFunctions();

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [guardLoading, setGuardLoading] = useState(true);

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [statusText, setStatusText] = useState(null);

  // Filters
  const [filterVendor, setFilterVendor] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPaidStatus, setFilterPaidStatus] = useState('');  // 'paid' | 'unpaid' | ''
  const [includeArchived, setIncludeArchived] = useState(false);

  // Add/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);  // null = creating
  const [saving, setSaving] = useState(false);

  // Two-tap archive confirm state — keyed by expenseId of the row armed
  const [archiveArmedId, setArchiveArmedId] = useState(null);

  // Summary tiles must compute before any conditional returns (React hooks rules)
  const totalCents = useMemo(
    () => expenses.reduce((sum, e) => sum + (e.amountCents || 0), 0),
    [expenses]
  );
  const unpaidCents = useMemo(
    () => expenses.filter(e => !e.paidAt).reduce((sum, e) => sum + (e.amountCents || 0), 0),
    [expenses]
  );

  // ----- Admin guard -----
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setIsAdmin(false);
        setGuardLoading(false);
        navigation.replace('Landing');
        return;
      }
      setUser(u);
      try {
        const token = await u.getIdTokenResult(true);
        const claimAdmin = token?.claims?.role === 'admin';
        const snap = await getDoc(doc(db, 'users', u.uid));
        const docAdmin = snap.exists() && snap.data()?.role === 'admin';
        setIsAdmin(claimAdmin || docAdmin);
      } catch {
        setIsAdmin(false);
      } finally {
        setGuardLoading(false);
      }
    });
    return unsub;
  }, [auth, navigation]);

  // ----- List fetch -----
  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setErrorText(null);
    try {
      const fn = httpsCallable(functions, 'adminListExpenses');
      const params = {
        includeArchived,
        limit: 200,
      };
      if (filterVendor) params.vendor = filterVendor;
      if (filterCategory) params.category = filterCategory;
      if (filterPaidStatus) params.paidStatus = filterPaidStatus;
      const result = await fn(params);
      setExpenses(result.data?.expenses || []);
    } catch (e) {
      console.error('[expenses-admin] list fetch failed:', e);
      setErrorText(`Failed to load expenses: ${e.message}`);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [functions, filterVendor, filterCategory, filterPaidStatus, includeArchived]);

  useEffect(() => {
    if (isAdmin) fetchExpenses();
  }, [isAdmin, fetchExpenses]);

  // ----- Save (create or update) -----
  const saveExpense = async (form, expenseId) => {
    setSaving(true);
    setErrorText(null);
    try {
      const amountCents = dollarsToCents(form.amount);
      if (!amountCents || amountCents <= 0) {
        throw new Error('Amount must be a positive number.');
      }
      const bpStart = dateStringToMs(form.billingPeriodStart);
      const bpEnd = dateStringToMs(form.billingPeriodEnd);
      const invDate = dateStringToMs(form.invoiceDate);
      const paidAt = dateStringToMs(form.paidAt);
      if (!bpStart || !bpEnd) throw new Error('Billing period start and end are required (YYYY-MM-DD).');
      if (!invDate) throw new Error('Invoice date is required (YYYY-MM-DD).');
      if (bpEnd < bpStart) throw new Error('Billing period end must be on or after start.');

      const payload = {
        vendor: form.vendor,
        category: form.category,
        description: form.description || null,
        amountCents,
        billingPeriodStart: bpStart,
        billingPeriodEnd: bpEnd,
        invoiceDate: invDate,
        invoiceId: form.invoiceId || null,
        paidFromAccount: form.paidFromAccount || 'pending',
        paidAt: paidAt || null,
      };

      if (expenseId) {
        const fn = httpsCallable(functions, 'adminUpdateExpense');
        await fn({ expenseId, ...payload });
        setStatusText(`Updated expense ${expenseId}.`);
      } else {
        const fn = httpsCallable(functions, 'adminCreateExpense');
        const result = await fn(payload);
        setStatusText(`Created expense ${result.data?.expenseId}.`);
      }

      setModalOpen(false);
      setEditingExpense(null);
      await fetchExpenses();
    } catch (e) {
      console.error('[expenses-admin] save failed:', e);
      setErrorText(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ----- Archive / Unarchive -----
  const archiveExpense = async (expenseId) => {
    setErrorText(null);
    try {
      const fn = httpsCallable(functions, 'adminArchiveExpense');
      await fn({ expenseId });
      setStatusText(`Archived expense ${expenseId}.`);
      setArchiveArmedId(null);
      await fetchExpenses();
    } catch (e) {
      console.error('[expenses-admin] archive failed:', e);
      setErrorText(`Archive failed: ${e.message}`);
    }
  };

  const unarchiveExpense = async (expenseId) => {
    setErrorText(null);
    try {
      const fn = httpsCallable(functions, 'adminUnarchiveExpense');
      await fn({ expenseId });
      setStatusText(`Restored expense ${expenseId}.`);
      await fetchExpenses();
    } catch (e) {
      console.error('[expenses-admin] unarchive failed:', e);
      setErrorText(`Unarchive failed: ${e.message}`);
    }
  };

  // ----- Invoice PDF upload (web-only, uses HTML file input) -----
  const uploadInvoice = async (expenseId) => {
    if (Platform.OS !== 'web') {
      setErrorText('Invoice upload is supported in the web admin only.');
      return;
    }
    setErrorText(null);

    // Programmatic file picker via DOM
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        // 1. Get signed upload URL
        const urlFn = httpsCallable(functions, 'adminGetInvoiceUploadUrl');
        const result = await urlFn({ expenseId, contentType: file.type || 'application/pdf' });
        const { uploadUrl, storagePath } = result.data || {};
        if (!uploadUrl) throw new Error('No upload URL returned.');

        // 2. PUT the file to the signed URL
        const putResp = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/pdf' },
          body: file,
        });
        if (!putResp.ok) throw new Error(`Upload failed (${putResp.status})`);

        // 3. Update the expense doc with the storage path
        const updateFn = httpsCallable(functions, 'adminUpdateExpense');
        await updateFn({ expenseId, invoiceUrl: storagePath });

        setStatusText(`Invoice uploaded for ${expenseId}.`);
        await fetchExpenses();
      } catch (err) {
        console.error('[expenses-admin] upload failed:', err);
        setErrorText(`Upload failed: ${err.message}`);
      }
    };
    input.click();
  };

  const downloadInvoice = async (expenseId) => {
    setErrorText(null);
    try {
      const fn = httpsCallable(functions, 'adminGetInvoiceDownloadUrl');
      const result = await fn({ expenseId });
      const { downloadUrl } = result.data || {};
      if (!downloadUrl) throw new Error('No download URL returned.');
      if (Platform.OS === 'web') {
        window.open(downloadUrl, '_blank');
      } else {
        setStatusText(`Download URL: ${downloadUrl}`);
      }
    } catch (e) {
      console.error('[expenses-admin] download failed:', e);
      setErrorText(`Download failed: ${e.message}`);
    }
  };

  // ----- Render guards -----
  if (guardLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}>
          <ActivityIndicator color={COLORS.primary} />
          <Text style={styles.dim}>Checking admin permissions…</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}>
          <Ionicons name="lock-closed" size={48} color={COLORS.danger} />
          <Text style={styles.warn}>Admin access required.</Text>
          <TouchableOpacity
            onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
            style={styles.backBtn}
          >
            <Text style={styles.backBtnText}>Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />
      <NavBar navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scroll}>
        {/* Title bar + Add button */}
        <View style={styles.titleRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>Operating Expenses</Text>
            <Text style={styles.subtitle}>Vendor invoices — manual entry. Stripe processor fees auto-tracked separately.</Text>
          </View>
          <Pressable
            onPress={() => { setEditingExpense(null); setModalOpen(true); }}
            style={({ pressed }) => [styles.addBtn, pressed && { opacity: 0.85 }]}
          >
            <Ionicons name="add" size={16} color={COLORS.bg} />
            <Text style={styles.addBtnText}>Add Expense</Text>
          </Pressable>
        </View>

        {errorText && (
          <View style={styles.errorBanner}>
            <Ionicons name="alert-circle" size={18} color={COLORS.danger} />
            <Text style={styles.errorText}>{errorText}</Text>
          </View>
        )}

        {statusText && !errorText && (
          <View style={styles.statusBanner}>
            <Ionicons name="checkmark-circle" size={18} color={COLORS.success} />
            <Text style={styles.statusText}>{statusText}</Text>
          </View>
        )}

        {/* Filter row */}
        <View style={styles.filterRow}>
          <FilterDropdown
            label="Vendor"
            value={filterVendor}
            onChange={setFilterVendor}
            options={[{ id: '', displayName: 'All vendors' }, ...VENDOR_OPTIONS]}
          />
          <FilterDropdown
            label="Category"
            value={filterCategory}
            onChange={setFilterCategory}
            options={[{ id: '', displayName: 'All categories' }, ...CATEGORY_OPTIONS]}
          />
          <FilterDropdown
            label="Paid status"
            value={filterPaidStatus}
            onChange={setFilterPaidStatus}
            options={[
              { id: '', displayName: 'Any' },
              { id: 'paid', displayName: 'Paid' },
              { id: 'unpaid', displayName: 'Unpaid' },
            ]}
          />
          <Pressable
            onPress={() => setIncludeArchived(prev => !prev)}
            style={({ pressed }) => [
              styles.toggleBtn,
              includeArchived && styles.toggleBtnActive,
              pressed && { opacity: 0.85 },
            ]}
          >
            <Ionicons
              name={includeArchived ? 'checkbox-outline' : 'square-outline'}
              size={16}
              color={includeArchived ? COLORS.primary : COLORS.textMuted}
            />
            <Text style={[styles.toggleBtnText, includeArchived && { color: COLORS.primary }]}>
              Show archived
            </Text>
          </Pressable>
        </View>

        {/* Summary tiles for the filtered page */}
        <View style={styles.summaryRow}>
          <SummaryTile label="Rows shown" value={String(expenses.length)} />
          <SummaryTile label="Total (filtered)" value={fmtCents(totalCents)} />
          <SummaryTile label="Unpaid (filtered)" value={fmtCents(unpaidCents)} tone={unpaidCents > 0 ? 'warn' : 'normal'} />
        </View>

        {/* Table */}
        {loading ? (
          <View style={styles.card}>
            <ActivityIndicator color={COLORS.primary} />
            <Text style={styles.dim}>Loading expenses…</Text>
          </View>
        ) : expenses.length === 0 ? (
          <View style={styles.card}>
            <Text style={styles.dim}>No expenses match the current filters. Click "Add Expense" to record one.</Text>
          </View>
        ) : (
          <View style={styles.tableCard}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeader, { flex: 1.5 }]}>Period</Text>
              <Text style={[styles.tableHeader, { flex: 1.5 }]}>Vendor</Text>
              <Text style={[styles.tableHeader, { flex: 3 }]}>Description</Text>
              <Text style={[styles.tableHeader, { flex: 1.2, textAlign: 'right' }]}>Amount</Text>
              <Text style={[styles.tableHeader, { flex: 1.5 }]}>Paid from</Text>
              <Text style={[styles.tableHeader, { flex: 2.5, textAlign: 'right' }]}>Actions</Text>
            </View>
            {expenses.map((e) => (
              <ExpenseRow
                key={e.expenseId}
                expense={e}
                onEdit={() => { setEditingExpense(e); setModalOpen(true); }}
                onArchive={() => archiveExpense(e.expenseId)}
                onUnarchive={() => unarchiveExpense(e.expenseId)}
                onUpload={() => uploadInvoice(e.expenseId)}
                onDownload={() => downloadInvoice(e.expenseId)}
                archiveArmed={archiveArmedId === e.expenseId}
                onArmArchive={() => setArchiveArmedId(e.expenseId)}
                onDisarmArchive={() => setArchiveArmedId(null)}
              />
            ))}
          </View>
        )}
      </ScrollView>

      {/* Add/Edit modal */}
      <ExpenseFormModal
        visible={modalOpen}
        expense={editingExpense}
        onClose={() => { setModalOpen(false); setEditingExpense(null); }}
        onSave={saveExpense}
        saving={saving}
      />

      <Footer navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function SummaryTile({ label, value, tone }) {
  const toneStyle = tone === 'warn' ? { color: COLORS.warning } : { color: COLORS.text };
  return (
    <View style={styles.summaryTile}>
      <Text style={styles.summaryLabel}>{label}</Text>
      <Text style={[styles.summaryValue, toneStyle]}>{value}</Text>
    </View>
  );
}

function FilterDropdown({ label, value, onChange, options }) {
  // RN Web renders <select> properly. For native, this falls back to a Pressable
  // cycle (good enough for admin tooling that runs primarily on web).
  if (Platform.OS === 'web') {
    return (
      <View style={styles.filterCell}>
        <Text style={styles.filterLabel}>{label}</Text>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={webSelectStyle}
        >
          {options.map(opt => (
            <option key={opt.id || '_all'} value={opt.id}>{opt.displayName}</option>
          ))}
        </select>
      </View>
    );
  }
  // Native fallback: cycle through options on tap
  const idx = options.findIndex(o => o.id === value);
  const next = options[(idx + 1) % options.length];
  return (
    <Pressable style={styles.filterCell} onPress={() => onChange(next.id)}>
      <Text style={styles.filterLabel}>{label}</Text>
      <Text style={styles.filterValue}>
        {options[idx >= 0 ? idx : 0].displayName}
      </Text>
    </Pressable>
  );
}

function ExpenseRow({
  expense, onEdit, onArchive, onUnarchive, onUpload, onDownload,
  archiveArmed, onArmArchive, onDisarmArchive,
}) {
  const cat = EXPENSE_CATEGORIES[expense.category] || {};
  const ven = EXPENSE_VENDORS[expense.vendor] || {};
  const paidFromLabel = PAID_FROM_ACCOUNTS[expense.paidFromAccount]?.displayName || expense.paidFromAccount;

  return (
    <View style={[styles.tableRow, expense.archived && styles.tableRowArchived]}>
      <View style={{ flex: 1.5 }}>
        <Text style={styles.cellPrimary}>{fmtMonth(expense.billingPeriodEnd)}</Text>
        <Text style={styles.cellSecondary}>inv. {fmtDate(expense.invoiceDate)}</Text>
      </View>

      <View style={{ flex: 1.5 }}>
        <View style={styles.vendorCell}>
          <Text style={styles.cellPrimary}>{ven.shortName || expense.vendor}</Text>
          {cat.color && (
            <View style={[styles.catBadge, { borderColor: cat.color }]}>
              <Text style={[styles.catBadgeText, { color: cat.color }]}>{cat.displayName}</Text>
            </View>
          )}
        </View>
      </View>

      <Text style={[styles.cellPrimary, { flex: 3 }]} numberOfLines={2}>
        {expense.description || <Text style={styles.cellSecondary}>—</Text>}
      </Text>

      <Text style={[styles.cellAmount, { flex: 1.2 }]}>{fmtCents(expense.amountCents)}</Text>

      <View style={{ flex: 1.5 }}>
        <Text style={styles.cellPrimary}>{paidFromLabel}</Text>
        {expense.paidAt
          ? <Text style={styles.cellSecondaryOk}>paid {fmtDate(expense.paidAt)}</Text>
          : <Text style={styles.cellSecondaryWarn}>unpaid</Text>}
      </View>

      <View style={[styles.actionsCell, { flex: 2.5 }]}>
        {!expense.archived ? (
          <>
            <Pressable onPress={onEdit} style={({ pressed }) => [styles.actionBtn, pressed && { opacity: 0.85 }]}>
              <Ionicons name="create-outline" size={14} color={COLORS.text} />
              <Text style={styles.actionBtnText}>Edit</Text>
            </Pressable>

            {expense.invoiceUrl ? (
              <Pressable onPress={onDownload} style={({ pressed }) => [styles.actionBtn, pressed && { opacity: 0.85 }]}>
                <Ionicons name="document-text-outline" size={14} color={COLORS.text} />
                <Text style={styles.actionBtnText}>PDF</Text>
              </Pressable>
            ) : (
              <Pressable onPress={onUpload} style={({ pressed }) => [styles.actionBtn, pressed && { opacity: 0.85 }]}>
                <Ionicons name="cloud-upload-outline" size={14} color={COLORS.textMuted} />
                <Text style={styles.actionBtnTextDim}>Upload</Text>
              </Pressable>
            )}

            {archiveArmed ? (
              <Pressable
                onPress={onArchive}
                style={({ pressed }) => [styles.actionBtnDanger, pressed && { opacity: 0.85 }]}
              >
                <Ionicons name="trash" size={14} color={COLORS.bg} />
                <Text style={styles.actionBtnDangerText}>Confirm</Text>
              </Pressable>
            ) : (
              <Pressable
                onPress={onArmArchive}
                style={({ pressed }) => [styles.actionBtn, pressed && { opacity: 0.85 }]}
              >
                <Ionicons name="trash-outline" size={14} color={COLORS.warning} />
                <Text style={styles.actionBtnTextWarn}>Archive</Text>
              </Pressable>
            )}

            {archiveArmed && (
              <Pressable onPress={onDisarmArchive} style={({ pressed }) => [styles.actionBtn, pressed && { opacity: 0.85 }]}>
                <Text style={styles.actionBtnTextDim}>Cancel</Text>
              </Pressable>
            )}
          </>
        ) : (
          <Pressable onPress={onUnarchive} style={({ pressed }) => [styles.actionBtn, pressed && { opacity: 0.85 }]}>
            <Ionicons name="arrow-undo-outline" size={14} color={COLORS.text} />
            <Text style={styles.actionBtnText}>Restore</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

function ExpenseFormModal({ visible, expense, onClose, onSave, saving }) {
  // Initialize form whenever modal opens or expense changes
  const isEditing = !!expense;
  const [form, setForm] = useState({
    vendor: 'gcp',
    category: 'infrastructure',
    description: '',
    amount: '',
    billingPeriodStart: '',
    billingPeriodEnd: '',
    invoiceDate: '',
    invoiceId: '',
    paidFromAccount: 'pending',
    paidAt: '',
  });

  useEffect(() => {
    if (!visible) return;
    if (expense) {
      setForm({
        vendor: expense.vendor || 'gcp',
        category: expense.category || EXPENSE_VENDORS[expense.vendor]?.defaultCategory || 'misc',
        description: expense.description || '',
        amount: expense.amountCents ? (expense.amountCents / 100).toFixed(2) : '',
        billingPeriodStart: msToDateString(expense.billingPeriodStart),
        billingPeriodEnd: msToDateString(expense.billingPeriodEnd),
        invoiceDate: msToDateString(expense.invoiceDate),
        invoiceId: expense.invoiceId || '',
        paidFromAccount: expense.paidFromAccount || 'pending',
        paidAt: msToDateString(expense.paidAt),
      });
    } else {
      // Default new-expense form: this month, GCP, infrastructure
      const now = new Date();
      const firstOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      const lastOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
      setForm({
        vendor: 'gcp',
        category: 'infrastructure',
        description: '',
        amount: '',
        billingPeriodStart: msToDateString(firstOfMonth.getTime()),
        billingPeriodEnd: msToDateString(lastOfMonth.getTime()),
        invoiceDate: msToDateString(Date.now()),
        invoiceId: '',
        paidFromAccount: 'pending',
        paidAt: '',
      });
    }
  }, [visible, expense]);

  const updateField = (key, value) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      // When vendor changes, auto-fill category from the vendor's default
      if (key === 'vendor') {
        next.category = EXPENSE_VENDORS[value]?.defaultCategory || prev.category;
      }
      return next;
    });
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          <Text style={styles.modalTitle}>{isEditing ? 'Edit Expense' : 'Add Expense'}</Text>

          <ScrollView style={styles.modalScroll}>
            <FormRow label="Vendor">
              <FormDropdown
                value={form.vendor}
                onChange={(v) => updateField('vendor', v)}
                options={VENDOR_OPTIONS}
              />
            </FormRow>

            <FormRow label="Category">
              <FormDropdown
                value={form.category}
                onChange={(v) => updateField('category', v)}
                options={CATEGORY_OPTIONS}
              />
            </FormRow>

            <FormRow label="Amount (USD)">
              <TextInput
                value={form.amount}
                onChangeText={(v) => updateField('amount', v)}
                placeholder="e.g., 342.18"
                placeholderTextColor={COLORS.textDim}
                style={styles.input}
                keyboardType="decimal-pad"
              />
            </FormRow>

            <FormRow label="Description">
              <TextInput
                value={form.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder="e.g., April GCP — Firestore + Cloud Functions"
                placeholderTextColor={COLORS.textDim}
                style={styles.input}
              />
            </FormRow>

            <View style={styles.formRow2}>
              <FormRow label="Period start (YYYY-MM-DD)" style={{ flex: 1 }}>
                <TextInput
                  value={form.billingPeriodStart}
                  onChangeText={(v) => updateField('billingPeriodStart', v)}
                  placeholder="2026-04-01"
                  placeholderTextColor={COLORS.textDim}
                  style={styles.input}
                />
              </FormRow>
              <FormRow label="Period end (YYYY-MM-DD)" style={{ flex: 1 }}>
                <TextInput
                  value={form.billingPeriodEnd}
                  onChangeText={(v) => updateField('billingPeriodEnd', v)}
                  placeholder="2026-04-30"
                  placeholderTextColor={COLORS.textDim}
                  style={styles.input}
                />
              </FormRow>
            </View>

            <FormRow label="Invoice date (YYYY-MM-DD)">
              <TextInput
                value={form.invoiceDate}
                onChangeText={(v) => updateField('invoiceDate', v)}
                placeholder="2026-05-01"
                placeholderTextColor={COLORS.textDim}
                style={styles.input}
              />
            </FormRow>

            <FormRow label="Invoice ID (optional)">
              <TextInput
                value={form.invoiceId}
                onChangeText={(v) => updateField('invoiceId', v)}
                placeholder="Vendor's invoice #"
                placeholderTextColor={COLORS.textDim}
                style={styles.input}
              />
            </FormRow>

            <FormRow label="Paid from">
              <FormDropdown
                value={form.paidFromAccount}
                onChange={(v) => updateField('paidFromAccount', v)}
                options={PAID_FROM_OPTIONS}
              />
            </FormRow>

            <FormRow label="Paid on (YYYY-MM-DD, leave blank if unpaid)">
              <TextInput
                value={form.paidAt}
                onChangeText={(v) => updateField('paidAt', v)}
                placeholder=""
                placeholderTextColor={COLORS.textDim}
                style={styles.input}
              />
            </FormRow>
          </ScrollView>

          <View style={styles.modalActions}>
            <Pressable onPress={onClose} style={({ pressed }) => [styles.modalBtnSecondary, pressed && { opacity: 0.85 }]}>
              <Text style={styles.modalBtnSecondaryText}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={() => onSave(form, expense?.expenseId)}
              disabled={saving}
              style={({ pressed }) => [styles.modalBtnPrimary, saving && { opacity: 0.6 }, pressed && !saving && { opacity: 0.85 }]}
            >
              {saving ? (
                <ActivityIndicator color={COLORS.bg} />
              ) : (
                <Text style={styles.modalBtnPrimaryText}>{isEditing ? 'Save Changes' : 'Create Expense'}</Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function FormRow({ label, children, style }) {
  return (
    <View style={[styles.formRow, style]}>
      <Text style={styles.formLabel}>{label}</Text>
      {children}
    </View>
  );
}

function FormDropdown({ value, onChange, options }) {
  if (Platform.OS === 'web') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} style={webSelectStyle}>
        {options.map(opt => (
          <option key={opt.id} value={opt.id}>{opt.displayName}</option>
        ))}
      </select>
    );
  }
  const idx = options.findIndex(o => o.id === value);
  const next = options[(idx + 1) % options.length];
  return (
    <Pressable style={styles.input} onPress={() => onChange(next.id)}>
      <Text style={{ color: COLORS.text }}>{options[idx >= 0 ? idx : 0].displayName}</Text>
    </Pressable>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const webSelectStyle = {
  backgroundColor: '#0a0a0a',
  color: '#ffffff',
  border: '1px solid #1f1f1f',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 13,
  outline: 'none',
  cursor: 'pointer',
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: COLORS.bg },
  scroll: { padding: 16, paddingBottom: 80 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg, padding: 24 },
  dim: { color: COLORS.textDim, marginTop: 8, fontSize: 13 },
  warn: { color: COLORS.danger, marginTop: 12, fontSize: 16, fontWeight: '600' },
  backBtn: { marginTop: 16, paddingVertical: 10, paddingHorizontal: 24, borderRadius: 6, borderWidth: 1, borderColor: COLORS.border },
  backBtnText: { color: COLORS.text, fontSize: 14 },

  titleRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 16, gap: 12 },
  title: { color: COLORS.text, fontSize: 24, fontWeight: '700' },
  subtitle: { color: COLORS.textMuted, fontSize: 13, marginTop: 2 },

  addBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: COLORS.primary, paddingVertical: 10, paddingHorizontal: 16,
    borderRadius: 6,
  },
  addBtnText: { color: COLORS.bg, fontSize: 14, fontWeight: '700' },

  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(239,68,68,0.1)', borderColor: COLORS.danger, borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 12,
  },
  errorText: { color: COLORS.danger, fontSize: 13, flex: 1 },

  statusBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(34,197,94,0.08)', borderColor: COLORS.success, borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 12,
  },
  statusText: { color: COLORS.success, fontSize: 13, flex: 1 },

  filterRow: {
    flexDirection: 'row', gap: 12, alignItems: 'flex-end',
    marginBottom: 12, flexWrap: 'wrap',
  },
  filterCell: { flexDirection: 'column', gap: 4, minWidth: 140 },
  filterLabel: { color: COLORS.textMuted, fontSize: 11, fontWeight: '600' },
  filterValue: {
    color: COLORS.text, backgroundColor: COLORS.cardAlt,
    borderColor: COLORS.border, borderWidth: 1, borderRadius: 6,
    paddingHorizontal: 10, paddingVertical: 8, fontSize: 13,
  },
  toggleBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.border, borderWidth: 1,
    borderRadius: 6, paddingHorizontal: 10, paddingVertical: 8,
  },
  toggleBtnActive: { borderColor: COLORS.primary },
  toggleBtnText: { color: COLORS.textMuted, fontSize: 13 },

  summaryRow: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  summaryTile: {
    flex: 1, minWidth: 140,
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.borderLight, borderWidth: 1,
    borderRadius: 6, padding: 10,
  },
  summaryLabel: { color: COLORS.textMuted, fontSize: 11, fontWeight: '600' },
  summaryValue: { color: COLORS.text, fontSize: 18, fontWeight: '700', marginTop: 2 },

  card: {
    backgroundColor: COLORS.card, borderColor: COLORS.border, borderWidth: 1,
    borderRadius: 8, padding: 16, marginBottom: 16, alignItems: 'center',
  },

  tableCard: {
    backgroundColor: COLORS.card, borderColor: COLORS.border, borderWidth: 1,
    borderRadius: 8, marginBottom: 16, overflow: 'hidden',
  },
  tableHeaderRow: {
    flexDirection: 'row', gap: 8, paddingHorizontal: 12, paddingVertical: 10,
    backgroundColor: COLORS.cardAlt, borderBottomColor: COLORS.border, borderBottomWidth: 1,
  },
  tableHeader: { color: COLORS.textMuted, fontSize: 11, fontWeight: '600', letterSpacing: 0.4 },
  tableRow: {
    flexDirection: 'row', gap: 8, paddingHorizontal: 12, paddingVertical: 12,
    borderBottomColor: COLORS.border, borderBottomWidth: 1, alignItems: 'center',
  },
  tableRowArchived: { opacity: 0.5 },
  cellPrimary: { color: COLORS.text, fontSize: 13 },
  cellSecondary: { color: COLORS.textMuted, fontSize: 11, marginTop: 2 },
  cellSecondaryOk: { color: COLORS.success, fontSize: 11, marginTop: 2 },
  cellSecondaryWarn: { color: COLORS.warning, fontSize: 11, marginTop: 2 },
  cellAmount: { color: COLORS.text, fontSize: 14, fontWeight: '700', textAlign: 'right', fontVariant: ['tabular-nums'] },

  vendorCell: { flexDirection: 'column', gap: 4 },
  catBadge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, borderWidth: 1, alignSelf: 'flex-start' },
  catBadgeText: { fontSize: 10, fontWeight: '600' },

  actionsCell: { flexDirection: 'row', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' },
  actionBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 5,
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.border, borderWidth: 1, borderRadius: 4,
  },
  actionBtnText: { color: COLORS.text, fontSize: 11 },
  actionBtnTextDim: { color: COLORS.textMuted, fontSize: 11 },
  actionBtnTextWarn: { color: COLORS.warning, fontSize: 11 },
  actionBtnDanger: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 5,
    backgroundColor: COLORS.danger, borderRadius: 4,
  },
  actionBtnDangerText: { color: COLORS.bg, fontSize: 11, fontWeight: '700' },

  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  modalCard: {
    backgroundColor: COLORS.card, borderColor: COLORS.borderLight, borderWidth: 1,
    borderRadius: 8, padding: 20, width: '100%', maxWidth: 540, maxHeight: '90%',
  },
  modalTitle: { color: COLORS.text, fontSize: 20, fontWeight: '700', marginBottom: 16 },
  modalScroll: { maxHeight: 480 },
  modalActions: {
    flexDirection: 'row', justifyContent: 'flex-end', gap: 10,
    marginTop: 16, borderTopColor: COLORS.borderLight, borderTopWidth: 1, paddingTop: 12,
  },
  modalBtnPrimary: {
    backgroundColor: COLORS.primary, paddingVertical: 10, paddingHorizontal: 18,
    borderRadius: 6, minWidth: 140, alignItems: 'center',
  },
  modalBtnPrimaryText: { color: COLORS.bg, fontSize: 14, fontWeight: '700' },
  modalBtnSecondary: {
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.border, borderWidth: 1,
    paddingVertical: 10, paddingHorizontal: 18, borderRadius: 6, alignItems: 'center',
  },
  modalBtnSecondaryText: { color: COLORS.text, fontSize: 14 },

  formRow: { marginBottom: 12 },
  formRow2: { flexDirection: 'row', gap: 8 },
  formLabel: { color: COLORS.textMuted, fontSize: 11, fontWeight: '600', marginBottom: 4 },
  input: {
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.border, borderWidth: 1,
    borderRadius: 6, paddingHorizontal: 10, paddingVertical: 8,
    color: COLORS.text, fontSize: 13,
  },
});
